import { DateTime } from "luxon";
import type { PeriodKey } from "@/types/filters";

export interface DateRange {
  startDate: string;
  endDate: string;
}

export type Granularity = "day" | "week" | "month";
export type PeriodFamily = "trailing" | "calendar";

// ── Weeks and week years (ISO 8601) ──────────────────────────────────────────
// Week 1 is the week containing Jan 4 — equivalently the week whose Thursday
// falls in January, equivalently the week holding most of its days in January.
// That is one rule, and it is the SAME rule the trend charts use to decide
// which month a week belongs to, so fiscal years and chart buckets can never
// disagree. Luxon implements it natively (weekYear / weekNumber /
// weeksInWeekYear), so there is no hand-rolled calendar maths here.
//
// A week year holds 52 or 53 weeks — 53 in 2026, 2032 and 2037. Week 53 has no
// counterpart in the prior year; getPriorYearRange returns null rather than
// borrowing week 52, which would show the same baseline twice.

const TRAILING_WEEKS: Partial<Record<PeriodKey, number>> = {
  T1W: 1,
  T4W: 4,
  T13W: 13,
  T52W: 52,
};

// Every period the dashboard offers. Listed once so a new period cannot be added
// without the rebuild window widening to include it.
export const ALL_PERIODS: PeriodKey[] = ["T1W", "T4W", "T13W", "T52W", "PM", "MTD", "YTD"];

export function getPeriodFamily(period: PeriodKey): PeriodFamily {
  return period in TRAILING_WEEKS ? "trailing" : "calendar";
}

function mondayOfWeek(weekYear: number, weekNumber: number): DateTime {
  return DateTime.fromObject({ weekYear, weekNumber, weekday: 1 });
}

function weeksInWeekYear(weekYear: number): number {
  return mondayOfWeek(weekYear, 1).weeksInWeekYear;
}

// The Sunday ending the most recently completed week relative to asOfDate.
function lastCompleteSunday(asOfDate: DateTime): DateTime {
  return asOfDate.startOf("week").minus({ days: 1 });
}

// ── Period ranges ─────────────────────────────────────────────────────────────

export function getPeriodDateRange(period: PeriodKey, asOfDate: DateTime): DateRange {
  const weeks = TRAILING_WEEKS[period];
  if (weeks !== undefined) {
    // Trailing family: always whole Mon–Sun weeks, so year-over-year compares
    // rotation against rotation rather than a five-Monday month against a four.
    const endDate = lastCompleteSunday(asOfDate);
    return {
      startDate: endDate.minus({ weeks }).plus({ days: 1 }).toISODate()!,
      endDate: endDate.toISODate()!,
    };
  }

  // Calendar family: counted in days, not weeks. It ends the day before the
  // As-of date because the nightly refresh lands a day behind. Anchoring the
  // month/year on that end date — not on As-of — is what keeps the range from
  // going empty on the 1st: As-of Aug 1 ends Jul 31, so MTD reads as all of
  // July rather than as nothing.
  const endDate = asOfDate.minus({ days: 1 });
  switch (period) {
    case "PM": {
      const prevMonth = endDate.startOf("month").minus({ months: 1 });
      return {
        startDate: prevMonth.toISODate()!,
        endDate: prevMonth.endOf("month").toISODate()!,
      };
    }
    case "MTD":
      return { startDate: endDate.startOf("month").toISODate()!, endDate: endDate.toISODate()! };
    case "YTD":
      return { startDate: endDate.startOf("year").toISODate()!, endDate: endDate.toISODate()! };
    default:
      throw new Error(`Unhandled period: ${period}`);
  }
}

// ── Prior year ────────────────────────────────────────────────────────────────

// The same week number one week year earlier, or null when that week does not
// exist — only possible for week 53.
function sameWeekPriorYear(date: DateTime): DateTime | null {
  const priorWeekYear = date.weekYear - 1;
  if (date.weekNumber > weeksInWeekYear(priorWeekYear)) return null;
  return mondayOfWeek(priorWeekYear, date.weekNumber);
}

// A range no row can fall in. Callers substitute it when the prior year has no
// counterpart, so every existing "no PY data" path — em dashes, EMPTY_METRICS —
// engages on its own instead of each call site special-casing null.
export const EMPTY_RANGE: DateRange = { startDate: "9999-12-31", endDate: "0001-01-01" };

// Returns null when there is no honest comparison to draw — a single week 53,
// which has no counterpart a year earlier. Callers render an em dash.
export function getPriorYearRange(range: DateRange, period: PeriodKey): DateRange | null {
  const start = DateTime.fromISO(range.startDate);
  const end = DateTime.fromISO(range.endDate);

  if (getPeriodFamily(period) === "calendar") {
    // Same calendar dates a year earlier. The day-of-week mix is NOT controlled
    // for — August 2026 has five Mondays where August 2025 has four — which is
    // inherent to comparing calendar months and is documented for the reader.
    return {
      startDate: start.minus({ years: 1 }).toISODate()!,
      endDate: end.minus({ years: 1 }).toISODate()!,
    };
  }

  const pyStart = sameWeekPriorYear(start);
  if (!pyStart) return null;

  // If only the END week is missing from the prior year, the window is a
  // multi-week one containing week 53. Clamp to the prior year's last week: the
  // current-year figure keeps all its weeks and the comparison runs one week
  // short, which matters for totals but not for rates.
  const pyEndMonday =
    sameWeekPriorYear(end) ?? mondayOfWeek(end.weekYear - 1, weeksInWeekYear(end.weekYear - 1));

  return { startDate: pyStart.toISODate()!, endDate: pyEndMonday.plus({ days: 6 }).toISODate()! };
}

// ── Denominators ──────────────────────────────────────────────────────────────

// Identifies the Mon–Sun week a date belongs to. Used to count DVM-weeks —
// distinct (doctor, week) pairs — which is what per-DVM-per-week metrics divide
// by. Counting distinct doctors instead assumes every doctor worked every week,
// so a changing roster dilutes the average: ten doctors for a fortnight and a
// different ten for the next fortnight reads as twenty doctors working all four
// weeks, halving the result.
export function getWeekKey(dateStr: string): string {
  return DateTime.fromISO(dateStr).startOf("week").toISODate()!;
}

// Weeks spanned by a range, as elapsed time rather than as a count of whole
// weeks. Trailing periods land on exact integers (T4W = 28 / 7 = 4); calendar
// periods land on fractions (a 9-day MTD is 1.29 weeks), which is the honest
// denominator — dividing nine days of hours by "one complete week" would
// overstate the weekly average by a third.
export function getWeeksInRange(startDate: string, endDate: string): number {
  const days = DateTime.fromISO(endDate).diff(DateTime.fromISO(startDate), "days").days + 1;
  return days / 7;
}

// ── Trend bucketing ───────────────────────────────────────────────────────────

export function getBucketKey(
  dateStr: string,
  granularity: Granularity,
  family: PeriodFamily
): string {
  const dt = DateTime.fromISO(dateStr);
  switch (granularity) {
    case "day":
      return dateStr;
    case "week":
      return dt.startOf("week").toISODate()!;
    case "month":
      // Trailing periods are built from whole weeks, so their months are too: a
      // week belongs to the month holding its Thursday (its majority of days),
      // and is never split across two buckets. Calendar periods are built from
      // days, so their months are plain calendar months.
      return family === "trailing"
        ? dt.startOf("week").plus({ days: 3 }).startOf("month").toISODate()!
        : dt.startOf("month").toISODate()!;
  }
}

// Whether a bucket should appear at all. Calendar periods do not start or end
// on week boundaries, so a week bucket at either edge would be a two-day stub
// drawn the same size as a seven-day week — and any per-week metric computed
// from it would be badly wrong. Those partial weeks are dropped; the KPI cards
// above still count every day, so a chart caption has to say so.
// Every bucket that overlaps the range is plotted, including a calendar period's
// part-weeks at each end.
//
// They used to be dropped: a two-day stub drawn the width of a full week made
// every per-week metric read ~3.5x low, because a doctor who worked both days
// still counted as one whole DVM-week. deriveTrendPoint now scales that
// denominator by the fraction of the week actually inside the range, so the rate
// is correct and the point can be shown -- which also means the chart covers the
// same days as the KPI cards above it, instead of silently fewer.
//
// Kept as a function rather than deleted: the callers read better for saying what
// they include, and a future granularity may need to exclude something.
export function isBucketIncluded(
  _bucketKey: string,
  _granularity: Granularity,
  _family: PeriodFamily,
  _range: DateRange
): boolean {
  return true;
}

// Day and week labels are two space-separated parts, which the charts split
// onto two axis lines: what the bucket is called on top, the date it starts on
// underneath. So the date always sits on the same line whichever grouping is
// selected, and the top line is the one that changes meaning — a weekday for
// days, a week number for weeks. Week numbers are also what the prior-year
// line is matched on, so seeing W21 tells you what the comparison is against.
export function getBucketLabel(bucketKey: string, granularity: Granularity): string {
  const dt = DateTime.fromISO(bucketKey);
  switch (granularity) {
    case "day":
      return dt.toFormat("ccc M/d");
    case "week":
      return `W${dt.weekNumber} ${dt.toFormat("M/d")}`;
    case "month":
      return dt.toFormat("MMM yy");
  }
}

export function getBucketRange(
  bucketKey: string,
  granularity: Granularity,
  family: PeriodFamily
): DateRange {
  const dt = DateTime.fromISO(bucketKey);
  switch (granularity) {
    case "day":
      return { startDate: bucketKey, endDate: bucketKey };
    case "week":
      return { startDate: bucketKey, endDate: dt.plus({ days: 6 }).toISODate()! };
    case "month": {
      if (family === "calendar") {
        return { startDate: bucketKey, endDate: dt.endOf("month").toISODate()! };
      }
      // Whole weeks whose Thursday lands in this month.
      let first = dt.startOf("week");
      if (first.plus({ days: 3 }).month !== dt.month) first = first.plus({ weeks: 1 });
      let last = dt.endOf("month").startOf("week");
      if (last.plus({ days: 3 }).month !== dt.month) last = last.minus({ weeks: 1 });
      return { startDate: first.toISODate()!, endDate: last.plus({ days: 6 }).toISODate()! };
    }
  }
}

// A grouping is only offered when the range covers at least two of its buckets —
// a chart with one point is not a trend. This is derived from the range rather
// than hardcoded per period, so it stays right when the As-of date moves.
/**
 * Every date the dashboard can show for a given As-of date, without anyone
 * changing that date: the union of every period and its prior-year match.
 *
 * ~730 days, and stable -- 728 at the end of August, 731 at New Year, 734 in
 * February. It is what "the dashboard is ready" actually means.
 *
 * NOT simply T52W, which is the intuitive answer and the wrong one. T52W ends at
 * the last COMPLETE Monday-Sunday week, while MTD and YTD run to the As-of date
 * itself, so T52W falls up to six days short of them -- measured at 4 of 6 sample
 * dates. Rebuilding T52W alone would leave the most recent days unfetched and
 * send anyone opening MTD straight into an on-demand pull.
 */
export function getFullDataWindow(asOf: DateTime): DateRange {
  let startDate = "9999-12-31";
  let endDate = "0001-01-01";
  for (const period of ALL_PERIODS) {
    const current = getPeriodDateRange(period, asOf);
    const priorYear = getPriorYearRange(current, period);
    const lo = priorYear ? priorYear.startDate : current.startDate;
    if (lo < startDate) startDate = lo;
    if (current.endDate > endDate) endDate = current.endDate;
  }
  return { startDate, endDate };
}

export function getAvailableGranularities(range: DateRange, family: PeriodFamily): Granularity[] {
  const start = DateTime.fromISO(range.startDate);
  const end = DateTime.fromISO(range.endDate);

  const bucketCount = (granularity: Granularity): number => {
    const keys = new Set<string>();
    for (let d = start; d <= end; d = d.plus({ days: 1 })) {
      const key = getBucketKey(d.toISODate()!, granularity, family);
      if (isBucketIncluded(key, granularity, family, range)) keys.add(key);
    }
    return keys.size;
  };

  // Lower bounds: a chart with one point is not a trend. Month needs three
  // rather than two because a two-point month chart is a comparison, not a
  // trend, and the week view of the same range always shows it better --
  // T4W spans exactly two months but four weeks.
  //
  // Upper bound on Day: past roughly a month of daily points the line stops
  // being readable. T13W is 91 points, YTD up to 365, T52W 364 -- none of them
  // legible, and all three are the queries that cost the most to compute.
  // Expressed as a bucket count rather than a per-period list so it stays
  // correct as the As-of date moves.
  const MAX_DAY_BUCKETS = 35;

  const available: Granularity[] = [];
  if (bucketCount("day") <= MAX_DAY_BUCKETS) available.push("day");
  if (bucketCount("week") >= 2) available.push("week");
  if (bucketCount("month") >= 3) available.push("month");
  return available;
}
